/**
 * Creates an SVG element with text that automatically fits within a given container.
 * @param text - The text content to display in the SVG
 * @param container - The container element to fit the text within
 * @param fill - The color of the text (defaults to "black")
 * @returns SVGElement containing the fitted text
 */
export function createSmartFittedTextSVG(
  text: string,
  container: HTMLElement,
  fill = 'black'
): SVGElement {
  if (!container) {
    throw new Error('Container element not found', container);
  }

  const { width, height } = container.getBoundingClientRect();
  const style = getComputedStyle(container);

  const computedWidth =
    width -
    parseFloat(style.borderWidth) * 2 -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight);
  const computedHeight =
    height -
    parseFloat(style.borderWidth) * 2 -
    parseFloat(style.paddingTop) -
    parseFloat(style.paddingBottom);

  const svgNS = 'http://www.w3.org/2000/svg';
  const xhtmlNS = 'http://www.w3.org/1999/xhtml';

  // Calculate optimal font size using a more sophisticated approach
  const fontSize = text.includes(' ')
    ? calculateMultiLineFontSize(text, computedWidth, computedHeight)
    : calculateOptimalFontSize(text, computedWidth, computedHeight);

  console.log(
    'createSmartFittedTextSVG',
    computedWidth,
    computedHeight,
    fontSize
  );

  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');

  const foreignObject = document.createElementNS(svgNS, 'foreignObject');
  foreignObject.setAttribute('x', '0');
  foreignObject.setAttribute('y', '0');
  foreignObject.setAttribute('width', computedWidth.toString());
  foreignObject.setAttribute('height', computedHeight.toString());

  const div = document.createElementNS(xhtmlNS, 'div');
  div.setAttribute('xmlns', xhtmlNS);
  div.style.fontSize = `${fontSize}px`;
  div.style.color = fill;
  div.style.wordWrap = 'break-word';
  div.style.display = 'flex';
  div.style.alignItems = 'center';
  div.style.justifyContent = 'center';
  div.style.textAlign = 'center';
  div.style.width = '100%';
  div.style.height = '100%';

  div.textContent = text;

  foreignObject.appendChild(div);
  svg.appendChild(foreignObject);

  return svg;
}

/**
 * Alternative mathematical approach (without DOM manipulation)
 * Use this if DOM manipulation is not preferred
 */

function calculateOptimalFontSize(
  text: string,
  containerWidth: number,
  containerHeight: number
): number {
  const minSize = 8;
  const maxSize = 192;
  if (containerWidth <= 0 || containerHeight <= 0 || !text) {
    return minSize;
  }

  const textLength = text.length;

  // Estimate character width (average character width in pixels)
  const avgCharWidth = 0.75; // Adjust based on font
  const lineHeight = 1.2; // Adjust based on font

  // Calculate font size based on width constraint
  const widthBasedSize = containerWidth / (textLength * avgCharWidth);

  // Calculate font size based on height constraint
  const estimatedLines = Math.ceil(
    textLength / (containerWidth / (widthBasedSize * avgCharWidth))
  );
  const heightBasedSize = containerHeight / (estimatedLines * lineHeight);

  // Use the smaller of the two to ensure text fits
  const fontSize = Math.min(widthBasedSize, heightBasedSize);

  return Math.max(minSize, Math.min(fontSize, maxSize));
}

/**
 * Calculates font size for multi-line text
 * @param text - The text content
 * @param containerWidth - Width of the container
 * @param containerHeight - Height of the container
 * @returns Optimal font size in pixels
 */
function calculateMultiLineFontSize(
  text: string,
  containerWidth: number,
  containerHeight: number
): number {
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.width = `${containerWidth * 0.95}px`; // Add some padding
  tempDiv.style.wordWrap = 'break-word';
  tempDiv.style.fontFamily = 'Arial, sans-serif';
  tempDiv.style.lineHeight = '1.2';
  tempDiv.style.whiteSpace = 'normal';
  tempDiv.style.overflowWrap = 'break-word';
  document.body.appendChild(tempDiv);

  // First, find a font size where the text actually wraps to multiple lines
  const lineRanges = findOptimalLineRanges(text, containerWidth);

  let minFontSize = 8;
  let maxFontSize = Math.min(containerWidth / 3, containerHeight / 2, 56); // More conservative max
  let optimalFontSize = minFontSize;

  while (minFontSize <= maxFontSize) {
    const midFontSize = Math.floor((minFontSize + maxFontSize) / 2);

    tempDiv.style.fontSize = `${midFontSize}px`;
    tempDiv.textContent = text;

    const maxLineWidth = getMaxLineWidthWithFixedBreaks(
      text,
      midFontSize,
      lineRanges
    );

    // Check both height and width constraints, and ensure line count consistency
    if (maxLineWidth <= containerWidth * 0.9) {
      optimalFontSize = midFontSize;
      minFontSize = midFontSize + 1;
    } else {
      maxFontSize = midFontSize - 1;
    }
  }

  document.body.removeChild(tempDiv);
  return Math.max(8, optimalFontSize);
}

/**
 * Find optimal line ranges by testing different font sizes to find where text wraps
 * @param text - The text content
 * @param containerWidth - Width of the container
 * @returns Array of line ranges [start, end] for each line
 */
function findOptimalLineRanges(
  text: string,
  containerWidth: number
): Array<[number, number]> {
  const words = text.split(' ');

  // Create a measurement span
  const measureSpan = document.createElement('span');
  measureSpan.style.position = 'absolute';
  measureSpan.style.visibility = 'hidden';
  measureSpan.style.fontFamily = 'Arial, sans-serif';
  measureSpan.style.whiteSpace = 'nowrap';
  document.body.appendChild(measureSpan);

  // Test font sizes to find where text starts wrapping to multiple lines
  let testFontSize = 8;
  let lineRanges: Array<[number, number]> = [];

  while (testFontSize <= 100) {
    // Reasonable upper limit
    measureSpan.style.fontSize = `${testFontSize}px`;

    // Calculate line ranges at this font size
    const currentRanges = calculateLineRanges(
      words,
      measureSpan,
      containerWidth
    );

    if (currentRanges.length > 1) {
      // Found a font size where text wraps to multiple lines
      lineRanges = currentRanges;
      break;
    }

    testFontSize += 2;
  }

  // If we never found multiple lines, use single line
  if (lineRanges.length === 0) {
    lineRanges = [[0, words.length - 1]];
  }

  document.body.removeChild(measureSpan);
  return lineRanges;
}

/**
 * Calculate line ranges for given words and font size
 * @param words - Array of words
 * @param measureSpan - Measurement element
 * @param containerWidth - Container width
 * @returns Array of line ranges
 */
function calculateLineRanges(
  words: string[],
  measureSpan: HTMLElement,
  containerWidth: number
): Array<[number, number]> {
  const lineRanges: Array<[number, number]> = [];
  let currentLineStart = 0;
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const space = i > 0 ? ' ' : '';
    const testText = currentLine + space + word;

    measureSpan.textContent = testText;
    const testWidth = measureSpan.offsetWidth;

    if (testWidth <= containerWidth) {
      // Word fits on current line
      currentLine = testText;
    } else {
      // Word doesn't fit, end current line and start new one
      if (currentLine) {
        lineRanges.push([currentLineStart, i - 1]);
      }
      currentLineStart = i;
      currentLine = word;
    }
  }

  // Add the last line
  if (currentLine) {
    lineRanges.push([currentLineStart, words.length - 1]);
  }

  return lineRanges;
}

/**
 * Calculate the width of the widest line using fixed line breaks
 * @param text - The text content
 * @param fontSize - Current font size being tested
 * @param containerWidth - Width of the container
 * @param lineRanges - Pre-determined line ranges
 * @returns Width of the widest line in pixels
 */
function getMaxLineWidthWithFixedBreaks(
  text: string,
  fontSize: number,
  lineRanges: Array<[number, number]>
): number {
  const words = text.split(' ');

  // Create a dedicated measurement element
  const measureSpan = document.createElement('span');
  measureSpan.style.position = 'absolute';
  measureSpan.style.visibility = 'hidden';
  measureSpan.style.fontSize = `${fontSize}px`;
  measureSpan.style.fontFamily = 'Arial, sans-serif';
  measureSpan.style.whiteSpace = 'nowrap';
  document.body.appendChild(measureSpan);

  let maxLineWidth = 0;

  // Calculate width for each line using the fixed ranges
  for (const [start, end] of lineRanges) {
    const lineWords = words.slice(start, end + 1);
    const lineText = lineWords.join(' ');

    measureSpan.textContent = lineText;
    const lineWidth = measureSpan.offsetWidth;

    if (lineWidth > maxLineWidth) {
      maxLineWidth = lineWidth;
    }
  }

  document.body.removeChild(measureSpan);
  return maxLineWidth;
}

/**
 * Normalize a string for equality comparison with an answer.
 */
export function normalizeForCompare(value: string): string {
  return value.replace(/[‘’]/g, "'").replace(/[“”]/g, '"').trim();
}

/**
 * Get the main domain (e.g. amiralearning.com) from a full domain (e.g. home.app.amiralearning.com)
 * @param domain full domain
 * @returns main domain
 */
export function getMainDomain(domain: string): string {
  if (domain.includes('localhost')) {
    return 'localhost';
  }

  const domainPartsReversed = domain.split('.').reverse();

  if (domainPartsReversed.length > 1) {
    return `${domainPartsReversed[1]}.${domainPartsReversed[0]}`;
  }

  return domain;
}

/**
 * Splits a string by a specified token and returns an object containing the last part after the token
 * and the remaining string.
 *
 * @param inputStr - The input string to parse
 * @param token - The token to split the string by
 * @returns An object containing:
 *          - remStr: The remaining string (all parts except the last, joined by the token)
 *          - value: The last part after the final token (empty string if token not found)
 *
 * @example
 * ```typescript
 * parseAndGetLastValueAfterToken("a/b/c", "/")
 * // returns { remStr: "a/b", value: "c" }
 *
 * parseAndGetLastValueAfterToken("abc", "/")
 * // returns { remStr: "abc", value: "" }
 * ```
 */
export function parseAndGetLastValueAfterToken(
  inputStr: string,
  token: string
): { remStr: string; value: string } {
  const parts = inputStr.split(token);
  if (parts.length < 2) {
    return { remStr: inputStr, value: '' };
  }

  const value = parts.pop() || '';
  const remStr = parts.join(token);

  return { remStr, value };
}
